/* @jsx h */
import { defineComponent, h, onBeforeUnmount, onMounted } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import {
  type GoogleOneTapController,
  type GoogleOneTapCredential,
  startGoogleOneTap,
} from "./googleOneTap";
import type { AuthenticationMode } from "./types";

// Brand buttons follow Apple's HIG and Google's Identity branding guidelines,
// which prescribe exact colors, so these intentionally bypass theme tokens.
const BUTTON_BASE =
  "relative flex h-12 w-full items-center justify-center gap-2.5 rounded-xl px-4 text-[15px] font-medium tracking-[-0.01em] outline-none transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base-100";

const AppleLogo = () => (
  <svg viewBox="0 0 814 1000" class="h-[18px] w-auto" aria-hidden="true">
    <path
      fill="currentColor"
      d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
    />
  </svg>
);

const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" class="h-[18px] w-[18px]" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

type SocialSignInButtonsProps = {
  mode: AuthenticationMode;
  onGoogleCredential: (credential: GoogleOneTapCredential) => void;
};

export const SocialSignInButtons = defineComponent<SocialSignInButtonsProps>({
  name: "SocialSignInButtons",
  props: ["mode", "onGoogleCredential"],
  setup(props) {
    const { t } = useLocalization();
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const appleUrl = `/api/oauth/apple/start?return_to=${encodeURIComponent(returnTo)}`;
    const googleUrl = `/api/oauth/google/start?return_to=${encodeURIComponent(returnTo)}`;

    // One Tap runs alongside the buttons: on FedCM browsers it's the native
    // account sheet. The Google button reopens that sheet and only follows
    // the redirect link when Google won't show it.
    let oneTap: GoogleOneTapController | null = null;
    onMounted(() => {
      oneTap = startGoogleOneTap({
        context: props.mode === "signin" ? "signin" : "signup",
        onCredential: (credential) => props.onGoogleCredential(credential),
      });
    });
    onBeforeUnmount(() => oneTap?.cancel());

    const onGoogleClick = (event: MouseEvent) => {
      const handled = oneTap?.promptOrFallback(() =>
        window.location.assign(googleUrl),
      );
      if (handled) {
        event.preventDefault();
      } else {
        // Still loading or unavailable: stop a late prompt from popping up
        // over the redirect, then let the link navigate.
        oneTap?.cancel();
      }
    };

    return () => (
      <div class="space-y-3">
        <a
          href={appleUrl}
          class={`${BUTTON_BASE} bg-black text-white shadow-sm hover:bg-[#1c1c1e] active:bg-[#2c2c2e]`}
        >
          <span class="-mt-0.5 flex">
            <AppleLogo />
          </span>
          <span>{t("customer.account.continue_with_apple")}</span>
        </a>

        <a
          href={googleUrl}
          onClick={onGoogleClick}
          class={`${BUTTON_BASE} border border-[#747775] bg-white text-[#1f1f1f] hover:bg-[#f8f9fa] active:bg-[#f1f3f4]`}
        >
          <GoogleLogo />
          <span>{t("customer.account.continue_with_google")}</span>
        </a>

        <div class="flex items-center gap-3 py-1" aria-hidden="true">
          <span class="h-px flex-1 bg-base-300" />
          <span class="text-xs font-medium">
            {t("customer.account.or_continue_email")}
          </span>
          <span class="h-px flex-1 bg-base-300" />
        </div>
      </div>
    );
  },
});
